import { useEffect } from "react";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import Slide from "@mui/material/Slide";

import { useDisclosure, useRemoveSupplier, useSupplierNetwork } from "hooks";

import { SupplierRow } from "./components/SupplierRow";
import { EvotruxDivider } from "../../../EvotruxDivider/EvotruxDivider";

interface Props {
  onAddSupplier: () => void;
  refresh?: boolean;
  onRefreshComplete?: () => void;
}

export function SupplierNetworkTable(props: Props) {
  const { onAddSupplier, refresh = false, onRefreshComplete } = props;
  const { data, loading, error, refetch } = useSupplierNetwork();
  const {
    loading: removeLoading,
    success: removeSuccess,
    removeSupplier,
    resetState,
  } = useRemoveSupplier();
  const {
    isOpen: snackbarOpened,
    onClose: onSnackbarClose,
    onOpen: openSnackbar,
  } = useDisclosure(false);

  useEffect(() => {
    if (removeSuccess) {
      openSnackbar();
      refetch();
      resetState();
    }
  }, [removeSuccess, refetch, resetState, openSnackbar]);

  useEffect(() => {
    if (refresh) {
      refetch();
      if (onRefreshComplete) {
        onRefreshComplete();
      }
    }
  }, [refresh, refetch, onRefreshComplete]);

  if (loading) {
    return (
      <Stack alignItems="center">
        <CircularProgress />
      </Stack>
    );
  }

  if (error) {
    return (
      <Stack>
        <Typography color="red">{error}</Typography>
      </Stack>
    );
  }

  if (data.length === 0) {
    return (
      <Stack spacing={2}>
        <Stack spacing={1}>
          <Typography variant="h5" fontWeight="600">
            Suppliers
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Shippers who can send shipments to you
          </Typography>
        </Stack>
        <EvotruxDivider />

        <Stack alignItems="center" py={4} spacing={2}>
          <Typography variant="body1" color="text.secondary">
            No suppliers in your network yet.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Add suppliers to start collaborating more efficiently.
          </Typography>
          <Button variant="contained" onClick={onAddSupplier} sx={{ mt: 2 }}>
            Add Supplier
          </Button>
        </Stack>
      </Stack>
    );
  }

  return (
    <>
      <Stack spacing={2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack spacing={1}>
            <Typography variant="h5" fontWeight="600">
              Suppliers ({data.length})
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Shippers who can send shipments to you
            </Typography>
          </Stack>

          <Button
            variant="contained"
            onClick={onAddSupplier}
            sx={{ minWidth: "140px" }}
          >
            Add Supplier
          </Button>
        </Stack>
        <EvotruxDivider />

        <Box
          sx={{
            maxHeight: "500px",
            overflowY: "scroll",
          }}
        >
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Contact</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Date Added</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((connection) => (
                  <SupplierRow
                    key={connection.id}
                    connection={connection}
                    onRemove={removeSupplier}
                    loading={removeLoading}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Stack>
      <Snackbar
        open={snackbarOpened}
        autoHideDuration={4000}
        onClose={onSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        TransitionComponent={Slide}
      >
        <Alert
          onClose={onSnackbarClose}
          severity="success"
          sx={{ width: "100%" }}
        >
          Supplier removed successfully!
        </Alert>
      </Snackbar>
    </>
  );
}
